import {GlobalEventBus} from "../../GlobalServices/GlobalEventBus";
import {
    CHECKOUT_EVENTS,
    CHECKOUT_MESSAGE_NAMES,
    CHECKOUT_MESSAGE_SOURCES,
    CheckoutClosedMessage,
    CheckoutMessageSource,
    CheckoutOpenedMessage
} from "../../Events/Checkout/events";

type CheckoutWindowMessage = {
    source?: unknown;
    event_source?: unknown;
    event?: unknown;
}

const isMessageObject = (data: unknown): data is CheckoutWindowMessage =>
    typeof data === 'object' && data !== null;

type UseCheckoutWindowEventsOptions = {
    eventBus: typeof GlobalEventBus;
    sources?: CheckoutMessageSource[];
    checkoutBaseUrl?: string;
}

const toOrigin = (url: string | undefined): string | undefined => {
    if (!url) { return undefined; }
    try {
        return new URL(url).origin;
    } catch {
        return undefined;
    }
}

export const useCheckoutWindowEvents = (
    {
        eventBus,
        sources = [CHECKOUT_MESSAGE_SOURCES.BOOKINGKIT_WIDGET],
        checkoutBaseUrl
    }: UseCheckoutWindowEventsOptions
) => {

    const checkoutOrigin = toOrigin(checkoutBaseUrl);

    const isFromCheckout = (origin: string) =>
        (!!checkoutOrigin && origin === checkoutOrigin) || origin === window.location.origin;

    const isKnownSource = (value: unknown): value is CheckoutMessageSource =>
        Object.values(CHECKOUT_MESSAGE_SOURCES).includes(value as CheckoutMessageSource);


    const getMessageSource = (messageData: CheckoutWindowMessage): CheckoutMessageSource | undefined =>
        [messageData?.source, messageData?.event_source].find(isKnownSource);

    const handleWidgetMessage = (messageData: CheckoutWindowMessage) => {
        const isThereAlsoAnOldWidget = !!window?.BookingKitApp;
        if (isThereAlsoAnOldWidget) { return; }

        eventBus.emit(CHECKOUT_EVENTS.MESSAGE_FROM_CHECKOUT, messageData);
    }

    const handleCheckoutMessage = (messageData: CheckoutWindowMessage, source: CheckoutMessageSource) => {
        if (messageData?.event === CHECKOUT_MESSAGE_NAMES.CHECKOUT_OPENED) {
            eventBus.emit(CHECKOUT_EVENTS.CHECKOUT_OPENED, {
                ...messageData,
                source,
                event: CHECKOUT_MESSAGE_NAMES.CHECKOUT_OPENED
            } as CheckoutOpenedMessage);
            return;
        }
        if (messageData?.event === CHECKOUT_MESSAGE_NAMES.CHECKOUT_CLOSED) {
            eventBus.emit(CHECKOUT_EVENTS.CHECKOUT_CLOSED, {
                ...messageData,
                source,
                event: CHECKOUT_MESSAGE_NAMES.CHECKOUT_CLOSED
            } as CheckoutClosedMessage);
        }
    }

    const parseMessageData = (data: unknown): CheckoutWindowMessage | undefined => {
        if (typeof data === 'string') {
            try {
                const parsed = JSON.parse(data);
                return isMessageObject(parsed) ? parsed : undefined;
            } catch {
                // Do nothing as this block is reached
                // when the message is not a valid JSON
                return undefined;
            }
        }

        return isMessageObject(data) ? data : undefined;
    }

    const messageListener = (e: MessageEvent) => {

        const messageData = parseMessageData(e.data);
        if (!messageData) { return; }

        const messageSource = getMessageSource(messageData);

        if (!messageSource || !sources.includes(messageSource)) {
            // Not a message for us;
            return;
        }

        if (messageSource === CHECKOUT_MESSAGE_SOURCES.BOOKINGKIT_CHECKOUT) {

            if (!isFromCheckout(e.origin)) {
                // Someone else claiming to be the checkout;
                return;
            }
            handleCheckoutMessage(messageData, messageSource);
            return;
        }

        handleWidgetMessage(messageData);
    }

    const startListening = () => window.addEventListener('message', messageListener);
    const stopListening = () => window.removeEventListener('message', messageListener);

    return {
        startListening,
        stopListening
    }
}

let externalCheckoutWindowEvents: ReturnType<typeof useCheckoutWindowEvents> | undefined;

export const listenToExternalCheckoutWindowEvents = (
    eventBus: typeof GlobalEventBus,
    checkoutBaseUrl: string | undefined
): void => {
    if (externalCheckoutWindowEvents) { return; }

    externalCheckoutWindowEvents = useCheckoutWindowEvents({
        eventBus,
        sources: [CHECKOUT_MESSAGE_SOURCES.BOOKINGKIT_CHECKOUT],
        checkoutBaseUrl
    });
    externalCheckoutWindowEvents.startListening();
}
